import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import MaskedTextField from '@/common/components/MaskedTextField'
import { customUnitMask, numberMask } from '@/common/constants/masks.constant'
import { CompanyBranchValidationSchema } from '@/features/companyBranches/schemas/companyBranch.schema'
import { TrashIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'

interface BranchConsumptionRestrictionProps {
  disabled?: boolean
}

export const BranchConsumptionRestriction = ({ disabled }: BranchConsumptionRestrictionProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CompanyBranchValidationSchema>()

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-4">
        <div>
          <p className="text-xs">Intervalo de tempo mínimo entre consumos</p>

          <ErrorMessage
            name="minimum_seconds_interval_between_consumptions"
            message={errors?.minimum_seconds_interval_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <Controller
            control={control}
            name='minimum_seconds_interval_between_consumptions.days'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(30)}
                label="Dias"
                {...field}
                errorMessage={errors?.minimum_seconds_interval_between_consumptions?.days?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_interval_between_consumptions.hours'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(23)}
                label="Horas"
                {...field}
                errorMessage={errors?.minimum_seconds_interval_between_consumptions?.hours?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_interval_between_consumptions.minutes'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Minutos"
                {...field}
                errorMessage={errors?.minimum_seconds_interval_between_consumptions?.minutes?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_interval_between_consumptions.seconds'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Segundos"
                {...field}
                errorMessage={errors?.minimum_seconds_interval_between_consumptions?.seconds?.message}
                disabled={disabled}
              />
            )}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('minimum_seconds_interval_between_consumptions.days', undefined)
              setValue('minimum_seconds_interval_between_consumptions.hours', undefined)
              setValue('minimum_seconds_interval_between_consumptions.minutes', undefined)
              setValue('minimum_seconds_interval_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>
      </div>

      <Controller
        control={control}
        name='minimum_kilometers_usage_between_consumptions'
        render={({ field }) => (
          <MaskedTextField
            className="grow"
            mask={customUnitMask('Km', 999999, false)}
            label="Intervalo Mínimo de Quilometragem entre consumos"
            {...field}
            errorMessage={errors?.minimum_kilometers_usage_between_consumptions?.message}
            disabled={disabled}
          />
        )}
      />

      <Controller
        control={control}
        name='maximum_kilometers_usage_between_consumptions'
        render={({ field }) => (
          <MaskedTextField
            className="grow"
            mask={customUnitMask('Km', 999999, false)}
            label="Intervalo Máximo de Quilometragem entre consumos"
            {...field}
            errorMessage={errors?.maximum_kilometers_usage_between_consumptions?.message}
            disabled={disabled}
          />
        )}
      />

      <div className="flex flex-col gap-y-4">
        <div>
          <p className="text-xs">Intervalo mínimo de Hora-Uso entre consumos</p>

          <ErrorMessage
            name="minimum_seconds_usage_between_consumptions"
            message={errors?.minimum_seconds_usage_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <Controller
            control={control}
            name='minimum_seconds_usage_between_consumptions.days'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(30)}
                label="Dias"
                {...field}
                errorMessage={errors?.minimum_seconds_usage_between_consumptions?.days?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_usage_between_consumptions.hours'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(23)}
                label="Horas"
                {...field}
                errorMessage={errors?.minimum_seconds_usage_between_consumptions?.hours?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_usage_between_consumptions.minutes'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Minutos"
                {...field}
                errorMessage={errors?.minimum_seconds_usage_between_consumptions?.minutes?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='minimum_seconds_usage_between_consumptions.seconds'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Segundos"
                {...field}
                errorMessage={errors?.minimum_seconds_usage_between_consumptions?.seconds?.message}
                disabled={disabled}
              />
            )}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('minimum_seconds_usage_between_consumptions.days', undefined)
              setValue('minimum_seconds_usage_between_consumptions.hours', undefined)
              setValue('minimum_seconds_usage_between_consumptions.minutes', undefined)
              setValue('minimum_seconds_usage_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>

        <div>
          <p className="text-xs">Intervalo máximo de Hora-Uso entre consumos</p>

          <ErrorMessage
            name="maximum_seconds_usage_between_consumptions"
            message={errors?.maximum_seconds_usage_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <Controller
            control={control}
            name='maximum_seconds_usage_between_consumptions.days'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(30)}
                label="Dias"
                {...field}
                errorMessage={errors?.maximum_seconds_usage_between_consumptions?.days?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='maximum_seconds_usage_between_consumptions.hours'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(23)}
                label="Horas"
                {...field}
                errorMessage={errors?.maximum_seconds_usage_between_consumptions?.hours?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='maximum_seconds_usage_between_consumptions.minutes'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Minutos"
                {...field}
                errorMessage={errors?.maximum_seconds_usage_between_consumptions?.minutes?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            control={control}
            name='maximum_seconds_usage_between_consumptions.seconds'
            render={({ field }) => (
              <MaskedTextField
                className="grow"
                mask={numberMask(59)}
                label="Segundos"
                {...field}
                errorMessage={errors?.maximum_seconds_usage_between_consumptions?.seconds?.message}
                disabled={disabled}
              />
            )}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('maximum_seconds_usage_between_consumptions.days', undefined)
              setValue('maximum_seconds_usage_between_consumptions.hours', undefined)
              setValue('maximum_seconds_usage_between_consumptions.minutes', undefined)
              setValue('maximum_seconds_usage_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>
      </div>
    </div>
  )
}
